import { config as funnelJourneyConfig } from 'funnel-journeys';
import Joi from 'joi';
import sjson from 'secure-json-parse';
import { Channels, Config } from 'server-schemas';
import { CurrencyPriority } from '../currencyPriority';
import { ClientSideError, ErrorRecovery } from '../error/error-utils';
import { get as getFeatureFlag } from '../featureFlags';
import { funnelJourneyStore, useFunnelJourneyConfigStore } from '../stores';

export type FunnelJourney = Channels.Basket.FunnelJourneyClientComponent & Channels.Basket.FunnelJourneyServerComponent;
/**
 * This module provides utility functions for handling cookies and headers related to the Funnel components.
 * It includes functions for setting and retrieving cookie and header names, as well as validating and parsing
 * the FunnelQuery cookie, which represents the user's search parameters.
 *
 * @module cookie-utils
 */

/**
 * Sets the names for cookies used in the Funnel component.
 *
 * @function
 * @param {Config.CookieNames} cookies - An object containing cookie names.
 *
 * export const setCookieNames = (cookies: Config.CookieNames) => {...};
 */
export let cookieNames: Config.CookieNames;
export const setCookieNames = (cookies: Config.CookieNames) => {
  cookieNames = cookies;
};

/**
 * Sets the names for headers used in the Funnel component.
 *
 * @function
 * @param {Config.HeaderNames} headers - An object containing header names.
 *
 * export const setHeaderNames = (headers: Config.HeaderNames) => {...};
 */
export let headerNames: Config.HeaderNames;
export const setHeaderNames = (headers: Config.HeaderNames) => {
  headerNames = headers;
};

/**
 * Defines error messages related to the Funnel component, such as validation errors for the FunnelQuery cookie.
 *
 * @constant
 * @type {Object}
 *
 * export const errorMessages = {...};
 */
export const errorMessages = {
  funnelQueryValidation: 'The FunnelQuery cookie connot be validated. Check the FunnelQuery cookie.'
};

/**
 * Reads browser cookies and converts them into an object.
 *
 * @function
 * @returns {Object} - An array of objects representing the cookies.
 *
 * export function serializeCookieAsJSON() {...}
 */
export function serializeCookieAsJSON(): Record<string, string> {
  return Object.hasOwn(globalThis, 'window') // check if we are in browser
    ? Object.fromEntries(
        document.cookie
          .split('; ')
          .reverse() // we reverse this because safari seems to duplicate some cookies and the old (expired!) one is 2nd in the list. Other 3rd part cookie libraries do something similar
          .map(v => v.split(/=([\s\S]*)/).map(decodeURIComponent))
      )
    : {};
}

/**
 * Defines the JSON schema for validating the FunnelQuery cookie.
 *
 * @constant
 * @type {Joi.ObjectSchema}
 *
 * export const FunnelQueryCookieJsonSchema = Joi.object({...});
 */

export const FunnelQueryCookieJsonSchema = Joi.object<{
  DeeplinkParams: string;
  /** OriginIata */
  Oi: string;
  /** DestinationIata */
  Di: string;
  /** OutboundDate */
  Od: string;
  /** ReturnDate */
  Rd?: string;
  /** OutboundFlightNumber */
  Ofn?: number;
  /** ReturnFlightNumber */
  Rfn?: number;
  /** FunnelJourney */
  Jrn: string;
  /** UsingFixedJourney */
  Fix: boolean;
  /** NumberOfAdults */
  Ad: number;
  /** NumberOfChildren */
  Ch: number;
  /** NumberOfInfants */
  In: number;
  /** OpenSearchPanel */
  Sp: boolean;
  /** DrawerTitle */
  Dr?: string;
  /** ShowFlexiFares */
  Flx: boolean;
  /** RemainOnStep1 */
  Rem: boolean;
  /** ComponentSender */
  Snd: string;
  /** CurrencyCode */
  Cur?: string;
  /** PaymentTypeCode */
  Pay?: string;
  /** FareClassCode */
  Fare?: string;
  languageCode?: string;
}>({
  DeeplinkParams: Joi.string(),
  Oi: Joi.string(),
  Di: Joi.string(),
  Od: Joi.string(),
  Rd: Joi.string().optional(),
  Ofn: Joi.number().optional(),
  Rfn: Joi.number().optional(),
  Jrn: Joi.string(),
  Fix: Joi.boolean(),
  Ad: Joi.number(),
  Ch: Joi.number(),
  In: Joi.number(),
  Sp: Joi.boolean(),
  Dr: Joi.string().optional(),
  Flx: Joi.boolean(),
  Rem: Joi.boolean(),
  Snd: Joi.string(),
  Cur: Joi.string().optional().allow(''), // CDT-1082: Skyscanner sends empty string when currency not supported by EJ
  Pay: Joi.string().optional(),
  Fare: Joi.string().optional(),
  languageCode: Joi.string().optional()
}).unknown(true);

type ExtractJoiType<T extends Joi.AnySchema> = T extends Joi.Schema<infer U> ? U : never;
export type FunnelQueryCookieJson = ExtractJoiType<typeof FunnelQueryCookieJsonSchema>;

/**
 * Represents the structure of the FunnelQuery cookie as an object with typed properties.
 *
 * @typedef {Object} FunnelQueryCookieObject
 * @property {string} deepLinkParams - The deep link parameters from the cookie.
 * @property {number} numberOfAdults - The number of adult passengers.
 * @property {number} numberOfChildren - The number of child passengers.
 * @property {number} numberOfInfants - The number of infant passengers.
 * @property {string} originIata - The IATA code for the origin airport.
 * @property {string} destinationIata - The IATA code for the destination airport.
 * @property {string} outboundDate - The date of the outbound flight.
 * @property {string} [returnDate] - The optional return date for round-trip flights.
 * @property {string} languageCode - The language code for the user's language.
 * @property {boolean} isOneWay - Indicates if the journey is one-way.
 * @property {string} componentSender - The sender component identifier.
 */
export type FunnelQueryCookieObject = {
  deepLinkParams: string;
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfInfants: number;
  originIata: string;
  destinationIata: string;
  outboundDate: string;
  returnDate?: string | null;
  languageCode?: string;
  isOneWay: boolean;
  currencyCode?: string;
  componentSender: string;
  // Todo: parse more entries
};

type FunnelJourneyConfigKeys = keyof typeof funnelJourneyConfig.journeys;

/**
 * Validates the provided FunnelQuery JSON against the defined schema.
 *
 * @function
 * @param {FunnelQueryCookieJson} json - The FunnelQuery JSON to validate.
 * @throws {string} Throws an error message if validation fails.
 * @returns {boolean} Returns true if validation is successful.
 *
 * export function validateFunnelQueryCookieJson(json: FunnelQueryCookieJson) {...}
 */
export function validateFunnelQueryCookieJson(json: FunnelQueryCookieJson) {
  const { error } = FunnelQueryCookieJsonSchema.validate(json, { stripUnknown: true });
  if (error) {
    throw new ClientSideError({ message: error.message, recovery: ErrorRecovery.EXIT_HOME_PAGE, showCorrelation: true });
  }
  return true;
}

let passThroughData: Channels.Basket.FunnelJourneyClientComponent = {
  Adults: 0,
  Children: 0,
  Infants: 0,
  PaymentType: null,
  Origin: '',
  Destination: '',
  Outbound: '',
  Return: '',
  FlexiFares: false,
  CurrencyPriority: CurrencyPriority.InferredFromRoute,
  JourneyPairId: 1,
  CarSearchQuery: '',
  FunnelJourney: '',
  HasSeenSeatingPage: false,
  OpenChangeSearch: false,
  HasServerBasket: true,
  DrawerTitle: false,
  BrowserDataSubmittedForTransactionId: '',
  Component: '',
  UsingFixedJourney: false
};

export const getFunnelJourney = () => localStorage.getItem(cookieNames.localStorage.journey);

export const getParsedFunnelJourney = () => sjson.parse(getFunnelJourney() || '{"value": {}}').value;

export const hasBasket = () => {
  if (typeof window !== 'undefined') {
    const queryCookie = readCookie(cookieNames.Availability.funnelQuery);
    const journey = window.localStorage.getItem(cookieNames.localStorage.journey);
    return journey && !queryCookie;
  }
  return false;
};

type FunnelJourneyKey = keyof typeof funnelJourneyConfig.journeys | '';

export const initialiseFunnelJourneyConfigStore = (journeyKey: FunnelJourneyKey) => {
  const funnelJourneyKeys = Object.keys(funnelJourneyConfig.journeys).map(key => key);
  const isValidJourneyKey = funnelJourneyKeys.includes(journeyKey);

  let journeyKeyToStore: FunnelJourneyKey = isValidJourneyKey ? journeyKey : '';
  let journeyItemsToStore = [];

  if (!isValidJourneyKey) {
    journeyKeyToStore = <FunnelJourneyKey>funnelJourneyConfig.default.funnelJourney;
  }

  journeyItemsToStore = funnelJourneyConfig.journeys[journeyKeyToStore as FunnelJourneyConfigKeys];

  useFunnelJourneyConfigStore.getState().setConfig({ key: journeyKeyToStore, steps: journeyItemsToStore });
};

export function parseFunnelQuery(): FunnelQueryCookieObject | undefined {
  const isFunnelJourneyConfigEnabled =
    new URLSearchParams(window.location.search).get('funnelJourneyConfig') === 'true' || getFeatureFlag('funnelJourneyConfig');
  const queryString = readCookie(cookieNames.Availability.funnelQuery) || '';
  const localData = getFunnelJourney();
  if (queryString) {
    if (localData) {
      localStorage.removeItem(cookieNames.localStorage.journey);
      deleteCookie(cookieNames.Availability.ejJourney, true);
    }
    //TODO: clean up any of cookies / local storage specific to pick flight page (e.g. LABI)
    const json: FunnelQueryCookieJson = sjson.parse(queryString);

    validateFunnelQueryCookieJson(json);

    const deeplinkParams = new URLSearchParams(json.DeeplinkParams || '');
    const languageCode = deeplinkParams.get('lang')?.toUpperCase();
    const currencyCode = (json.Cur || undefined)?.toUpperCase(); // CDT-1082: force empty strings to be treated as undefined

    if (isFunnelJourneyConfigEnabled) {
      initialiseFunnelJourneyConfigStore(<FunnelJourneyKey>json.Jrn);
    }

    const isOneWay = !json.Rd;

    passThroughData = {
      ...passThroughData,
      Adults: json.Ad,
      Children: json.Ch,
      Infants: json.In,
      Origin: json.Oi,
      Destination: json.Di,
      Outbound: json.Od,
      Return: json.Rd,
      UsingFixedJourney: Boolean(json.Fix),
      CurrencyPriority: currencyCode ? CurrencyPriority.SetByDeepLink : CurrencyPriority.InferredFromRoute,
      FunnelJourney: json.Jrn,
      languageCode,
      Component: json.Snd
    };

    return {
      deepLinkParams: json.DeeplinkParams as string,
      componentSender: json.Snd,
      numberOfAdults: json.Ad,
      numberOfChildren: json.Ch,
      numberOfInfants: json.In,
      originIata: json.Oi,
      destinationIata: json.Di,
      outboundDate: json.Od,
      languageCode,
      isOneWay,
      currencyCode,
      // This ReturnDate is being used when toggling between one way and round trip in the ChangeSearch component
      returnDate: json.Rd
    };
  } else if (localData) {
    const json: FunnelJourney = sjson.parse(localData).value;
    funnelJourneyStore.getState().setValue(json);
    if (isFunnelJourneyConfigEnabled) {
      initialiseFunnelJourneyConfigStore(<FunnelJourneyKey>json.FunnelJourney);
    }

    passThroughData = {
      ...passThroughData,
      Adults: json.Adults,
      Children: json.Children,
      Infants: json.Infants,
      Origin: json.Origin,
      Destination: json.Destination,
      Outbound: json.Outbound,
      Return: json.Return,
      UsingFixedJourney: json.UsingFixedJourney,
      languageCode: json.languageCode,
      CurrencyPriority: json.CurrencyPriority,
      FunnelJourney: json.FunnelJourney,
      Component: json.Component
    };

    return {
      deepLinkParams: '',
      componentSender: '',
      numberOfAdults: json.Adults,
      numberOfChildren: json.Children,
      numberOfInfants: json.Infants,
      originIata: json.Origin,
      destinationIata: json.Destination,
      outboundDate: json.Outbound,
      languageCode: json.languageCode,
      isOneWay: !json.Return,
      currencyCode: json.CurrencyCode,
      returnDate: json.Return
    };
  }

  return undefined;
}

/**
 * Reads a specific cookie from the browser's cookies using the provided key.
 *
 * @function
 * @param {string} key - The key of the cookie to read.
 * @returns {*} - The value of the cookie associated with the provided key.
 *
 * export const readCookie = (key: string) => {...};
 */
export const readCookie = (key: string) => {
  if (typeof window !== 'undefined') {
    const cookies = serializeCookieAsJSON();

    return cookies[key];
  }

  return undefined;
};

export const setJourneyData = (serverValue: Channels.Basket.FunnelJourneyServerComponent) => {
  const currentLocalStorage = localStorage.getItem(cookieNames.localStorage.journey);
  let current: FunnelJourney | undefined = undefined;
  if (currentLocalStorage) {
    current = sjson.parse(currentLocalStorage)?.value;
  }

  const outboundDate: string = serverValue.Pairs[0]?.Outbound || current?.Pairs[0]?.Outbound || passThroughData.Outbound;
  const returnDate: string | null = serverValue.Pairs[0]?.Return || current?.Pairs[0]?.Return || passThroughData.Return || null;
  /**
   * Server value is generate from basket, it won't have a reference to market groups
   * Use the info we have already persisted (from funnel query) to augment this
   */
  serverValue.Pairs = serverValue.Pairs.map(pair => ({
    ...pair,
    Outbound: outboundDate,
    Return: returnDate,
    Origin: passThroughData.Origin,
    Destination: passThroughData.Destination
  }));

  const value: FunnelJourney = {
    ...serverValue,
    FlightsOffers: getFlightsOffers(serverValue, current),
    ...passThroughData,
    Outbound: outboundDate,
    Return: returnDate
  };
  funnelJourneyStore.getState().setValue(value);
};

const getFlightsOffers = (serverValue: Channels.Basket.FunnelJourneyServerComponent, currentLocalStorage: any) => {
  const FlightsOffers = {
    // Retain the old offers just in case they are relevant
    ...(currentLocalStorage ? currentLocalStorage.FlightsOffers : {}),
    ...(serverValue.FlightsOffers ? serverValue.FlightsOffers : {})
  };

  return FlightsOffers;
};

export const readSessionExpiryCookie = async () => {
  // only do this if we have a basket. safari has problems cleaning up cookies on prod meaning some people might see an out of date cookie
  if (hasBasket()) {
    return new Promise(resolve => {
      let sessionExpirationTime = readCookie(cookieNames.Rebooking.sessionExpiry);
      if (sessionExpirationTime) {
        resolve(sessionExpirationTime);
      } else {
        // if we couldn't read the expiry time then try again after small delay in case it wasn't set in time (race condition)
        setTimeout(() => {
          sessionExpirationTime = readCookie(cookieNames.Rebooking.sessionExpiry);
          resolve(sessionExpirationTime);
        }, 100);
      }
    });
  }
  return Promise.resolve(readCookie('dlxSessionExpiry'));
};

export type CookieOptions = {
  domain?: string;
  encode?: boolean;
  /** cookie date string or number in days */
  expires?: string | number;
  path?: string;
  sameSite?: string;
  secure?: boolean;
};

export const setCookie = (name: string, value: string, { encode, ...options }: CookieOptions = {}) => {
  if (typeof document !== 'undefined') {
    const cookieValue = encode ? encodeURIComponent(value) : value;

    options.sameSite = options.sameSite ?? 'Lax';
    options.secure = window.location.hostname === 'localhost' ? false : (options.secure ?? true);

    if (typeof options.expires === 'number') {
      const expiresDate = new Date();
      expiresDate.setDate(expiresDate.getDate() + options.expires);
      options.expires = expiresDate.toUTCString();
    }

    const cookieOptions = Object.entries(options).map(([optionKey, optionValue]) => {
      const domainSet = (optionKey === 'domain' && optionValue !== undefined) || null;
      if (!optionValue && !domainSet) return null;
      return optionValue === true ? optionKey : `${optionKey}=${optionValue}`;
    });

    const cookieName = encode ? encodeURIComponent(name) : name;
    document.cookie = `${cookieName}=${cookieValue};${cookieOptions.filter(Boolean).join(';')}`;
  }
};

const expireCookie = (name: string) => {
  setCookie(name, '', { expires: -1 });
};

const expireCookieWithEmptyDomain = (name: string) => {
  setCookie(name, '', { expires: -1, domain: '' });
  setCookie(name, '', { expires: -1, domain: '', path: `/` });
};
const expireCookieWithDomains = (name: string) => {
  const domainsToIgnore = ['com', 'co.uk'];
  const splitHostname = window.location.hostname.split('.');
  // e.g. ['www', 'easyjet', 'com']

  while (splitHostname.length > 0) {
    const domain = splitHostname.join('.');
    // e.g. "www.easyjet.com"

    if (domainsToIgnore.includes(domain)) break;

    setCookie(name, '', { expires: -1, domain });
    setCookie(name, '', { expires: -1, domain, path: `/` });
    setCookie(name, '', { expires: -1, domain: `.${domain}` });
    setCookie(name, '', { expires: -1, domain: `.${domain}`, path: `/` });

    splitHostname.shift();
  }
};

// ref: da-mamba/Frontend/src/Shared/Scripts/AngularEj/Services/Cookies/BaseCookieService.ts
export const deleteCookie = (name: string, forAllDomains = false) => {
  expireCookie(name);
  if (forAllDomains && typeof window !== 'undefined') {
    expireCookieWithEmptyDomain(name);
    expireCookieWithDomains(name);
  }
};

export const buildFunnelJourneyComponent = (funnelQueryCookieObject: FunnelQueryCookieObject) => {
  return {
    Adults: funnelQueryCookieObject.numberOfAdults,
    Children: funnelQueryCookieObject.numberOfChildren,
    Infants: funnelQueryCookieObject.numberOfInfants,
    Return: funnelQueryCookieObject.returnDate,
    CurrencyCode: funnelQueryCookieObject.currencyCode || '',
    Destination: funnelQueryCookieObject.destinationIata,
    Origin: funnelQueryCookieObject.originIata,
    isReturnJourney: !!funnelQueryCookieObject.returnDate,
    Pairs: [
      {
        Id: 1,
        IsPrimary: false,
        Outbound: funnelQueryCookieObject.outboundDate,
        Return: funnelQueryCookieObject.returnDate || null,
        Destination: funnelQueryCookieObject.destinationIata,
        Origin: funnelQueryCookieObject.originIata,
        BaseCurrency: funnelQueryCookieObject.currencyCode || ''
      }
    ],
    FareTypeFlights: []
  };
};
